import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faShieldHalved,
  faCheckCircle,
  faCircleXmark,
  faPlus,
  faTimes,
  faTrash,
  faDownload,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDate } from '../../utils/http';

interface ChecklistItem {
  category: string;
  question: string;
  section: string;
  evidenceCount: number;
  hasValidEvidence: boolean;
}

interface Evidence {
  id: string;
  category: string;
  title: string;
  description: string;
  fileUrl: string | null;
  fileName: string | null;
  validFrom: string | null;
  validUntil: string | null;
  licensedLearners: number | null;
  uploadedAt: string;
  isExpired: boolean;
}

interface Overview {
  checklist: ChecklistItem[];
  evidence: Evidence[];
}

/** Admin: QCTO compliance evidence register, checked against the QCTO LMS requirements. */
export default function AdminCompliancePage() {
  const { accessToken } = useAuth();
  const [data, setData] = useState<Overview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [licensedLearners, setLicensedLearners] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<Overview>(accessToken, '/api/admin/compliance');
    if (res.ok && res.data) {
      setData(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load compliance register.');
    }
    setIsLoading(false);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (preset?: string) => {
    setCategory(preset ?? data?.checklist[0]?.category ?? '');
    setTitle('');
    setDescription('');
    setValidFrom('');
    setValidUntil('');
    setLicensedLearners('');
    setFile(null);
    setFormError('');
    setShowForm(true);
  };

  const save = async () => {
    setFormError('');
    if (!category || !title.trim()) {
      setFormError('Category and title are required.');
      return;
    }
    const form = new FormData();
    form.append('category', category);
    form.append('title', title);
    form.append('description', description);
    if (validFrom) form.append('validFrom', new Date(validFrom).toISOString());
    if (validUntil) form.append('validUntil', new Date(validUntil).toISOString());
    if (licensedLearners) form.append('licensedLearners', licensedLearners);
    if (file) form.append('file', file);

    setIsSaving(true);
    const res = await authFetch(accessToken, '/api/admin/compliance', { method: 'POST', form });
    setIsSaving(false);
    if (!res.ok) {
      setFormError(res.message);
      return;
    }
    setShowForm(false);
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this evidence record?')) return;
    const res = await authFetch(accessToken, `/api/admin/compliance/${id}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  const checklist = data?.checklist ?? [];
  const covered = checklist.filter((c) => c.hasValidEvidence).length;
  const sections = Array.from(new Set(checklist.map((c) => c.section)));

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">QCTO Compliance</h1>
          <p className="text-sm text-brand-900/60 mt-1">Evidence register for the LMS site visit</p>
        </div>
        <button
          onClick={() => openForm()}
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
        >
          <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
          Add evidence
        </button>
      </div>

      <div className="p-8 space-y-6">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : (
          data && (
            <>
              <div className="card-surface p-5 flex items-center gap-4 max-w-xl">
                <FontAwesomeIcon icon={faShieldHalved} className="h-8 w-8 text-brand-600" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-brand-950">
                    {covered} of {checklist.length} requirements have valid evidence
                  </p>
                  <div className="w-full bg-canvas-200 rounded-full h-2 mt-2">
                    <div className="bg-brand-gradient h-2 rounded-full" style={{ width: `${checklist.length ? (covered / checklist.length) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>

              {sections.map((section) => (
                <div key={section}>
                  <h2 className="text-xs font-bold uppercase tracking-wider text-brand-900/50 mb-2">{section}</h2>
                  <div className="card-surface divide-y divide-canvas-200 overflow-hidden">
                    {checklist
                      .filter((c) => c.section === section)
                      .map((c) => (
                        <div key={c.category} className="px-5 py-4 flex items-start gap-3">
                          <FontAwesomeIcon
                            icon={c.hasValidEvidence ? faCheckCircle : c.evidenceCount > 0 ? faTriangleExclamation : faCircleXmark}
                            className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                              c.hasValidEvidence ? 'text-green-600' : c.evidenceCount > 0 ? 'text-amber-500' : 'text-red-400'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-brand-950">{c.category}</p>
                            <p className="text-xs text-brand-900/60">{c.question}</p>
                            {c.evidenceCount > 0 && !c.hasValidEvidence && (
                              <p className="text-xs text-amber-600 mt-1">Evidence on file has expired.</p>
                            )}
                          </div>
                          <button
                            onClick={() => openForm(c.category)}
                            className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex-shrink-0"
                          >
                            {c.evidenceCount > 0 ? 'Add more' : 'Add evidence'}
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-brand-900/50 mb-2">Evidence on file</h2>
                {data.evidence.length === 0 ? (
                  <div className="card-surface p-8 text-center text-brand-900/60 text-sm">Nothing uploaded yet.</div>
                ) : (
                  <div className="card-surface divide-y divide-canvas-200 overflow-hidden">
                    {data.evidence.map((e) => (
                      <div key={e.id} className="px-5 py-4 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-brand-950">
                            {e.title}
                            {e.isExpired && <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase">Expired</span>}
                          </p>
                          <p className="text-xs text-brand-900/50">
                            {e.category}
                            {e.validUntil && ` · valid until ${formatDate(e.validUntil)}`}
                            {e.licensedLearners != null && ` · ${e.licensedLearners} licensed learners`}
                          </p>
                          {e.description && <p className="text-xs text-brand-900/70 mt-1">{e.description}</p>}
                          {e.fileUrl && (
                            <a href={e.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 mt-2">
                              <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
                              {e.fileName ?? 'Download'}
                            </a>
                          )}
                        </div>
                        <button onClick={() => remove(e.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0" title="Delete">
                          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )
        )}
      </div>

      {showForm && data && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">Add evidence</h3>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {data.checklist.map((c) => (
                  <option key={c.category} value={c.category}>
                    {c.section} · {c.category}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (e.g. Microsoft 365 licence agreement)"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Notes: counts, operating systems, backup frequency, provider names, etc."
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-brand-950 mb-1">Valid from</label>
                  <input
                    type="date"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-950 mb-1">Valid until</label>
                  <input
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    className="w-full px-3 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
              <input
                type="number"
                value={licensedLearners}
                onChange={(e) => setLicensedLearners(e.target.value)}
                placeholder="Licensed learners (LMS licence only)"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Supporting document</label>
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
              </div>
              {formError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{formError}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
